import * as React from 'react';
import styled from 'styled-components';

const DEFAULT_VISIBLE = 2;

type Props = {
  value: any;
  allLabel?: string;
  visibleChips?: number;
};

const ChipListCell = ({
  value,
  allLabel = 'All',
  visibleChips = DEFAULT_VISIBLE,
}: Props) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!value) return null;
  if (value === 'All') {
    return (
      <ChipRow>
        <Chip $tone="all">{allLabel}</Chip>
      </ChipRow>
    );
  }

  const names = String(value)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (names.length === 0) return null;

  const visible = expanded ? names : names.slice(0, visibleChips);
  const remaining = names.length - visibleChips;

  return (
    <ChipRow>
      {visible.map(name => (
        <Chip key={name}>{name}</Chip>
      ))}
      {remaining > 0 && (
        <MoreChip
          type="button"
          onClick={() => setExpanded(prev => !prev)}
        >
          {expanded ? 'Show less' : `+${remaining} more`}
        </MoreChip>
      )}
    </ChipRow>
  );
};

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  max-width: 320px;
`;

const Chip = styled.span<{ $tone?: 'all' }>`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === 'all' ? 'var(--success-50)' : 'var(--primary-50)'};
  color: ${({ $tone }) =>
    $tone === 'all' ? 'var(--success-700)' : 'var(--primary-700)'};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'all' ? 'var(--success-100)' : 'var(--primary-100)'};
`;

const MoreChip = styled.button`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: var(--neutral-100);
  color: var(--neutral-700);
  border: 1px solid var(--border);
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-200);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

export default ChipListCell;
